/**
 * AutoFix.tsx — Auto-Fix Center
 *
 * Security fix requests grouped by scan, with severity/tier/provider filters.
 * Default view: Critical + High issues only — clear, calm, actionable.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SummarizeIcon from '@mui/icons-material/Summarize';

import CreateTicketDialog from '../components/CreateTicketDialog';
import { useAuth } from '../context/AuthContext';

// ── Types ────────────────────────────────────────────────────────────────────

interface Customer {
  id: string;
  name: string;
  organisation: string;
}

interface FixRequest {
  id: string;
  finding_id: string;
  finding_title: string;
  finding_severity: string;
  finding_resource_id: string;
  provider: string;
  tier: string;
  status: string;
  ai_explanation: string;
  ai_instructions: string;
  approved_at: string | null;
  executed_at: string | null;
  before_state: Record<string, unknown>;
  after_state: Record<string, unknown>;
  created_at: string;
  scan_id: string | null;
  scan_date: string | null;
  error_message: string | null;
  result_message: string | null;
  attempt_count: number;
}

interface ScanGroup {
  scanId: string;
  scanDate: string;
  provider: string;
  fixes: FixRequest[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const SEV_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };
const TIER_ORDER: Record<string, number> = { automatic: 0, guided: 1, managed: 2 };

const PROVIDER_COLORS: Record<string, string> = {
  aws: '#f77f00', gcp: '#2dc653', azure: '#00b4d8', ssh: '#9c6ade',
};

const PROVIDER_LABELS: Record<string, string> = {
  aws: 'AWS', gcp: 'GCP', azure: 'Azure', ssh: 'Private Server',
};

const TIER_INFO: Record<string, { label: string; color: 'success' | 'info' | 'warning'; desc: string }> = {
  automatic: { label: '⚡ Auto-fixable', color: 'success', desc: 'Done in seconds — no action needed after approval' },
  guided:    { label: '📋 Step-by-step', color: 'info',    desc: 'We walk you through every step' },
  managed:   { label: '🔧 Managed fix',  color: 'warning', desc: 'A security engineer handles this for you' },
};

type ChipColor = 'error' | 'warning' | 'info' | 'success' | 'default';

const SEVERITY_COLOR: Record<string, ChipColor> = {
  critical: 'error', high: 'error', medium: 'warning', low: 'info', informational: 'default',
};

const STATUS_CHIP: Record<string, { label: string; color: ChipColor; tagline: string }> = {
  done:      { label: '✅ Done',       color: 'success', tagline: 'Shield fixed this automatically' },
  failed:    { label: '❌ Failed',     color: 'error',   tagline: 'Auto-fix failed' },
  executing: { label: '⚙️ Executing', color: 'warning', tagline: 'Fix in progress…' },
  approved:  { label: 'Approved',      color: 'info',    tagline: 'Approved — awaiting execution' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function ProviderBadge({ provider }: { provider: string }) {
  return (
    <Chip
      label={PROVIDER_LABELS[provider] ?? provider.toUpperCase()}
      size="small"
      sx={{ fontWeight: 700, bgcolor: PROVIDER_COLORS[provider] ?? '#555', color: '#000', fontSize: '0.7rem' }}
    />
  );
}

function TierBadge({ tier, onClick }: { tier: string; onClick?: () => void }) {
  const info = TIER_INFO[tier] ?? { label: tier, color: 'default' as const };
  return (
    <Chip
      label={info.label}
      color={info.color}
      size="small"
      variant="outlined"
      clickable={!!onClick}
      onClick={onClick}
      sx={{ fontWeight: 600, fontSize: '0.7rem', ...(onClick && { cursor: 'pointer' }) }}
    />
  );
}

function formatScanDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatValue(v: unknown): string {
  if (v === undefined || v === null) return '(none)';
  if (Array.isArray(v)) {
    return v.map((item) => {
      if (typeof item === 'object' && item !== null) {
        const r = item as Record<string, unknown>;
        if ('IpProtocol' in r) {
          const proto = r.IpProtocol === '-1' ? 'All' : String(r.IpProtocol).toUpperCase();
          const ports = r.FromPort != null && r.ToPort != null
            ? (r.FromPort === 0 && r.ToPort === 65535 ? 'All ports' : `${r.FromPort}-${r.ToPort}`)
            : '';
          const cidrs = (r.IpRanges as Array<Record<string, unknown>> | undefined)?.map((c) => c.CidrIp).join(', ') ?? '';
          return `${proto} ${ports} ${cidrs}`.trim();
        }
        return JSON.stringify(r);
      }
      return String(item);
    }).join('; ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function formatStateChange(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const allKeys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  for (let i = 0; i < allKeys.length; i++) {
    const key = allKeys[i];
    const bVal = before[key];
    const aVal = after[key];
    if (JSON.stringify(bVal) !== JSON.stringify(aVal)) {
      const label = key.replace(/_/g, ' ');
      lines.push(`${label}: ${formatValue(bVal)} → ${formatValue(aVal)}`);
    }
  }
  return lines;
}

// ── Fix Card ─────────────────────────────────────────────────────────────────

function FixCard({
  fr, mode, onApprove, approving, onCreateTicket, onRetry, retrying,
}: {
  fr: FixRequest;
  mode: 'pending' | 'history';
  onApprove: (id: string) => void;
  approving: boolean;
  onCreateTicket: (fr: FixRequest) => void;
  onRetry: (id: string) => void;
  retrying: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const tierInfo = TIER_INFO[fr.tier];
  const showTicketButton = mode === 'pending' && (fr.tier === 'guided' || fr.tier === 'managed');
  const statusChip = STATUS_CHIP[fr.status];
  const isDone = fr.status === 'done';
  const isFailed = fr.status === 'failed';
  const isExecuting = fr.status === 'executing';
  const showExplanation = mode === 'pending' || (mode === 'history' && fr.status === 'approved');

  const stateChanges = isDone ? formatStateChange(fr.before_state ?? {}, fr.after_state ?? {}) : [];

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: fr.finding_severity === 'critical' ? 'error.dark'
          : fr.finding_severity === 'high' ? 'warning.dark'
          : 'divider',
        p: 2, mb: 1,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={0.5}>
            <ProviderBadge provider={fr.provider} />
            <TierBadge
              tier={fr.tier}
              onClick={fr.tier === 'guided' ? () => { if (fr.ai_instructions) setExpanded(e => !e); else onApprove(fr.id); } : undefined}
            />
            <Chip
              label={fr.finding_severity.toUpperCase()}
              color={SEVERITY_COLOR[fr.finding_severity] ?? 'default'}
              size="small"
              sx={{ fontWeight: 700, fontSize: '0.65rem' }}
            />
            {mode === 'history' && statusChip && (
              <Chip
                label={
                  fr.tier === 'guided' && fr.status === 'approved'
                    ? '📋 Guide Available'
                    : statusChip.label
                }
                color={statusChip.color}
                size="small"
                sx={{
                  fontWeight: 600, fontSize: '0.65rem',
                  ...(isExecuting && {
                    '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }),
                }}
              />
            )}
          </Stack>

          <Typography variant="body1" fontWeight={600} gutterBottom>
            {fr.finding_title}
          </Typography>

          {fr.finding_resource_id && (
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', display: 'block', mb: 0.5 }}>
              {fr.finding_resource_id.length > 60 ? fr.finding_resource_id.slice(0, 60) + '…' : fr.finding_resource_id}
            </Typography>
          )}

          {/* ── Done ── */}
          {mode === 'history' && isDone && (
            <>
              <Typography variant="body2" color="success.main" fontWeight={500} mb={0.5}>
                {statusChip.tagline}
              </Typography>
              {fr.executed_at && (
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Fixed on {formatScanDate(fr.executed_at)}
                </Typography>
              )}
              {stateChanges.length > 0 && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(46, 125, 50, 0.06)', borderRadius: 1, border: 1, borderColor: 'success.light' }}>
                  {stateChanges.map((line, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" display="block">
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* ── Executing ── */}
          {mode === 'history' && isExecuting && (
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <CircularProgress size={16} color="warning" />
              <Typography variant="body2" color="warning.main" fontWeight={500}>
                {statusChip.tagline}
              </Typography>
            </Box>
          )}

          {/* ── Failed ── */}
          {mode === 'history' && isFailed && (
            <>
              <Typography variant="body2" color="error.main" fontWeight={500} mb={0.5}>
                {statusChip.tagline}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Attempt {fr.attempt_count || 1} of 3
              </Typography>
              {(fr.result_message || fr.error_message) && (
                <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                  <Typography variant="body2">
                    {fr.result_message || 'An error occurred during the fix.'}
                  </Typography>
                  {fr.error_message && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {fr.error_message}
                    </Typography>
                  )}
                </Alert>
              )}
              {fr.ai_instructions && (
                <Box mt={0.5}>
                  <Button
                    size="small"
                    startIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                    onClick={() => setExpanded(!expanded)}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                    color="primary"
                  >
                    {expanded ? 'Hide manual steps' : 'Fix Manually — view steps'}
                  </Button>
                  <Collapse in={expanded}>
                    <Box mt={1} p={1.5} sx={{ bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider', whiteSpace: 'pre-wrap' }}>
                      <Typography variant="caption" color="text.secondary">{fr.ai_instructions}</Typography>
                    </Box>
                  </Collapse>
                </Box>
              )}
            </>
          )}

          {/* ── Pending / Approved content ── */}
          {showExplanation && (
            <>
              {fr.ai_explanation && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {fr.ai_explanation}
                </Typography>
              )}
              {tierInfo && mode === 'pending' && (
                <Typography variant="caption" color="text.disabled">{tierInfo.desc}</Typography>
              )}
              {fr.ai_instructions && (
                <Box mt={0.5}>
                  <Button
                    size="small"
                    startIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                    onClick={() => setExpanded(!expanded)}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', color: 'text.secondary' }}
                  >
                    {expanded ? 'Hide steps' : 'View fix steps'}
                  </Button>
                  <Collapse in={expanded}>
                    <Box mt={1} p={1.5} sx={{ bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider', whiteSpace: 'pre-wrap' }}>
                      <Typography variant="caption" color="text.secondary">{fr.ai_instructions}</Typography>
                    </Box>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* ── Action buttons ── */}
        {mode === 'pending' && (
          <Stack spacing={1} alignItems="flex-end" flexShrink={0}>
            {fr.tier === 'guided' ? (
              <>
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  startIcon={approving
                    ? <CircularProgress size={14} color="inherit" />
                    : <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                  onClick={() => { if (fr.ai_instructions) setExpanded(e => !e); else onApprove(fr.id); }}
                  disabled={approving}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {approving ? 'Loading…' : expanded ? 'Hide Steps' : 'View Step-by-step Guide'}
                </Button>
                {expanded && fr.ai_instructions && (
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={approving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                    onClick={() => onApprove(fr.id)}
                    disabled={approving}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Done — Approve
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={approving ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
                onClick={() => onApprove(fr.id)}
                disabled={approving}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {approving ? 'Approving…' : 'Approve Fix'}
              </Button>
            )}
            {showTicketButton && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<ConfirmationNumberIcon />}
                onClick={() => onCreateTicket(fr)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Let Shield Fix It For Me
              </Button>
            )}
          </Stack>
        )}

        {mode === 'history' && isFailed && (
          <Stack spacing={1} alignItems="flex-end" flexShrink={0}>
            {(fr.attempt_count || 1) < 3 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                onClick={() => onRetry(fr.id)}
                disabled={retrying}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Retry Fix
              </Button>
            )}
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<ConfirmationNumberIcon />}
              onClick={() => onCreateTicket(fr)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Contact Support
            </Button>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

// ── Scan Group Card ──────────────────────────────────────────────────────────

function ScanGroupCard({
  group, mode, expanded, onToggle, onApprove, approving, onCreateTicket, onRetry, retrying,
}: {
  group: ScanGroup;
  mode: 'pending' | 'history';
  expanded: boolean;
  onToggle: () => void;
  onApprove: (id: string) => void;
  approving: Record<string, boolean>;
  onCreateTicket: (fr: FixRequest) => void;
  onRetry: (id: string) => void;
  retrying: Record<string, boolean>;
}) {
  const criticalCount  = group.fixes.filter(f => f.finding_severity === 'critical').length;
  const highCount      = group.fixes.filter(f => f.finding_severity === 'high').length;
  const autoCount      = group.fixes.filter(f => f.tier === 'automatic').length;
  const doneCount      = group.fixes.filter(f => f.status === 'done').length;
  const failedCount    = group.fixes.filter(f => f.status === 'failed').length;
  const executingCount = group.fixes.filter(f => f.status === 'executing').length;

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, cursor: 'pointer',
          bgcolor: expanded ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={700}>
              {PROVIDER_LABELS[group.provider] ?? group.provider.toUpperCase()} Scan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &mdash; {formatScanDate(group.scanDate)}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
              {group.scanId.slice(0, 8)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {criticalCount > 0 && (
              <Chip label={`${criticalCount} Critical`} color="error" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {highCount > 0 && (
              <Chip label={`${highCount} High`} color="warning" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {mode === 'pending' && autoCount > 0 && (
              <Chip label={`${autoCount} Auto-fixable`} color="success" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {mode === 'history' && doneCount > 0 && (
              <Chip label={`✅ ${doneCount} Done`} color="success" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {mode === 'history' && executingCount > 0 && (
              <Chip label={`⚙️ ${executingCount} Executing`} color="warning" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {mode === 'history' && failedCount > 0 && (
              <Chip label={`❌ ${failedCount} Failed`} color="error" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            <Chip label={`${group.fixes.length} total`} size="small" sx={{ fontSize: '0.65rem' }} />
          </Stack>
        </Box>
        <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s', color: 'text.secondary', flexShrink: 0 }} />
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {group.fixes.map((fr) => (
            <FixCard
              key={fr.id}
              fr={fr}
              mode={mode}
              onApprove={onApprove}
              approving={!!approving[fr.id]}
              onCreateTicket={onCreateTicket}
              onRetry={onRetry}
              retrying={!!retrying[fr.id]}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AutoFix() {
  const { user, loading: authLoading } = useAuth();
  const isCustomer = !authLoading && user?.role === 'customer';

  const [customers,        setCustomers]        = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [fixRequests,      setFixRequests]       = useState<FixRequest[]>([]);
  const [loading,          setLoading]          = useState(false);
  const [approving,        setApproving]        = useState<Record<string, boolean>>({});
  const [retrying,         setRetrying]         = useState<Record<string, boolean>>({});
  const [error,            setError]            = useState<string | null>(null);
  const [approveResult,    setApproveResult]    = useState<string | null>(null);
  const [tab,              setTab]              = useState(0);
  const [summary,          setSummary]          = useState<string | null>(null);
  const [summaryLoading,   setSummaryLoading]   = useState(false);
  const [ticketTarget,     setTicketTarget]     = useState<FixRequest | null>(null);
  const [remediationEnabled, setRemediationEnabled] = useState<boolean | null>(null);

  const [filterSeverity,  setFilterSeverity]  = useState<string>('critical_high');
  const [filterTier,      setFilterTier]      = useState<string>('all');
  const [providerFilter,  setProviderFilter]  = useState<string>('all');
  const [expandedScans,   setExpandedScans]   = useState<Set<string>>(new Set());
  const initialExpandDone = useRef(false);

  // ── Auth / data loading ────────────────────────────────────────────────────

  useEffect(() => {
    if (authLoading) return;
    if (isCustomer) setSelectedCustomer(user?.customer_id ?? '');
  }, [authLoading, isCustomer, user?.customer_id]);

  useEffect(() => {
    if (authLoading || isCustomer) return;
    axios.get<Customer[]>(`${API_BASE}/v1/customers/`)
      .then((r) => { setCustomers(r.data); if (r.data.length > 0) setSelectedCustomer(r.data[0].id); })
      .catch(() => setError('Could not load customers'));
  }, [authLoading, isCustomer]);

  useEffect(() => {
    if (!selectedCustomer) return;
    axios.get(`${API_BASE}/v1/autofix/${selectedCustomer}/remediation-status`)
      .then(r => setRemediationEnabled(r.data.remediation_enabled ?? false))
      .catch(() => setRemediationEnabled(false));
  }, [selectedCustomer]);

  const load = useCallback(async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get<FixRequest[]>(`${API_BASE}/v1/autofix/${selectedCustomer}/history`);
      setFixRequests(r.data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? 'Failed to load fix requests');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer]);

  useEffect(() => { load(); }, [load]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const approvefix = async (fixId: string) => {
    if (!selectedCustomer) return;
    setApproving((p) => ({ ...p, [fixId]: true }));
    setApproveResult(null);
    try {
      const r = await axios.post(`${API_BASE}/v1/autofix/${selectedCustomer}/approve/${fixId}`);
      setApproveResult(r.data.message ?? 'Fix approved.');
      await load();
    } catch (err: any) {
      setApproveResult(err.response?.data?.detail ?? 'Failed to approve fix');
    } finally {
      setApproving((p) => ({ ...p, [fixId]: false }));
    }
  };

  const retryFix = async (fixId: string) => {
    if (!selectedCustomer) return;
    setRetrying((p) => ({ ...p, [fixId]: true }));
    setApproveResult(null);
    try {
      const r = await axios.post(`${API_BASE}/v1/autofix/${selectedCustomer}/retry/${fixId}`);
      setApproveResult(r.data.message ?? 'Fix re-queued.');
      await load();
    } catch (err: any) {
      setApproveResult(err.response?.data?.detail ?? 'Failed to retry fix');
    } finally {
      setRetrying((p) => ({ ...p, [fixId]: false }));
    }
  };

  const generateSummary = async () => {
    if (!selectedCustomer) return;
    setSummaryLoading(true);
    setSummary(null);
    try {
      const r = await axios.post(`${API_BASE}/v1/autofix/${selectedCustomer}/summary`, {
        provider: providerFilter === 'all' ? 'all' : providerFilter,
        score: 0,
        customer_name: customers.find((c) => c.id === selectedCustomer)?.name ?? 'your organisation',
      });
      setSummary(r.data.summary);
    } catch (err: any) {
      setSummary(err.response?.data?.detail ?? 'Could not generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const pending = useMemo(() => fixRequests.filter(fr => fr.status === 'pending'), [fixRequests]);
  const history = useMemo(() => fixRequests.filter(fr => fr.status !== 'pending'), [fixRequests]);

  const applyFilters = useCallback((items: FixRequest[]) => {
    return items
      .filter(fr => {
        if (filterSeverity === 'all') return true;
        if (filterSeverity === 'critical_high') return fr.finding_severity === 'critical' || fr.finding_severity === 'high';
        return fr.finding_severity === filterSeverity;
      })
      .filter(fr => filterTier === 'all' || fr.tier === filterTier)
      .filter(fr => providerFilter === 'all' || fr.provider === providerFilter);
  }, [filterSeverity, filterTier, providerFilter]);

  const groupByScan = useCallback((items: FixRequest[]): ScanGroup[] => {
    const groups = new Map<string, FixRequest[]>();
    for (const fr of items) {
      const key = fr.scan_id || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(fr);
    }
    return Array.from(groups.entries())
      .map(([scanId, fixes]) => ({
        scanId,
        scanDate: fixes[0]?.scan_date || '',
        provider: fixes[0]?.provider || '',
        fixes: [...fixes].sort((a, b) => {
          const sevDiff = (SEV_ORDER[a.finding_severity] ?? 9) - (SEV_ORDER[b.finding_severity] ?? 9);
          if (sevDiff !== 0) return sevDiff;
          return (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9);
        }),
      }))
      .sort((a, b) => {
        if (!a.scanDate) return 1;
        if (!b.scanDate) return -1;
        return new Date(b.scanDate).getTime() - new Date(a.scanDate).getTime();
      });
  }, []);

  const allPendingGroups = useMemo(() => groupByScan(pending), [pending, groupByScan]);
  const latestPendingGroup = allPendingGroups.length > 0 ? allPendingGroups[0] : null;
  const criticalCount = latestPendingGroup ? latestPendingGroup.fixes.filter(fr => fr.finding_severity === 'critical').length : 0;
  const highCount     = latestPendingGroup ? latestPendingGroup.fixes.filter(fr => fr.finding_severity === 'high').length : 0;
  const autoCount     = latestPendingGroup ? latestPendingGroup.fixes.filter(fr => fr.tier === 'automatic').length : 0;
  const guidedCount   = latestPendingGroup ? latestPendingGroup.fixes.filter(fr => fr.tier === 'guided').length : 0;

  const filteredPending = useMemo(() => applyFilters(pending), [pending, applyFilters]);
  const filteredHistory = useMemo(() => applyFilters(history), [history, applyFilters]);
  const pendingGroups   = useMemo(() => groupByScan(filteredPending), [filteredPending, groupByScan]);
  const historyGroups   = useMemo(() => groupByScan(filteredHistory), [filteredHistory, groupByScan]);

  useEffect(() => {
    if (!initialExpandDone.current && pendingGroups.length > 0) {
      setExpandedScans(new Set([pendingGroups[0].scanId]));
      initialExpandDone.current = true;
    }
  }, [pendingGroups]);

  const toggleScan = (scanId: string) => {
    setExpandedScans(prev => {
      const next = new Set(prev);
      if (next.has(scanId)) next.delete(scanId);
      else next.add(scanId);
      return next;
    });
  };

  const hasExecuting = useMemo(() => fixRequests.some(fr => fr.status === 'executing'), [fixRequests]);

  useEffect(() => {
    if (!hasExecuting) return;
    const interval = setInterval(() => { load(); }, 5000);
    return () => clearInterval(interval);
  }, [hasExecuting, load]);

  const severityScore: Record<string, number> = {
    critical: 95, high: 75, medium: 50, low: 25, informational: 10,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4">Auto-Fix Center</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Shield fixes security issues automatically &mdash; no technical knowledge needed
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {approveResult && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setApproveResult(null)}>
          {approveResult}
        </Alert>
      )}

      {remediationEnabled === false && selectedCustomer && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" href="/settings">Enable in Settings</Button>
        }>
          <strong>Auto-fix is not active.</strong> Automatic fixes require remediation credentials.
          Without them, all fixes show step-by-step instructions instead.
        </Alert>
      )}

      {/* Summary bar — scoped to most recent scan */}
      {latestPendingGroup && (
        <Box mb={2}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" mb={0.5}>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              Latest Scan &mdash; {formatScanDate(latestPendingGroup.scanDate)}
            </Typography>
            {criticalCount > 0 && <Chip label={`${criticalCount} Critical`} color="error" size="small" sx={{ fontWeight: 700 }} />}
            {highCount > 0 && <Chip label={`${highCount} High`} color="warning" size="small" sx={{ fontWeight: 700 }} />}
            {autoCount > 0 && <Chip label={`${autoCount} Auto-fixable`} color="success" size="small" variant="outlined" sx={{ fontWeight: 700 }} />}
            {guidedCount > 0 && <Chip label={`${guidedCount} Guided`} color="info" size="small" variant="outlined" sx={{ fontWeight: 700 }} />}
          </Stack>
          {allPendingGroups.length > 1 && (
            <Typography variant="caption" color="text.disabled">
              Showing fixes from {allPendingGroups.length} scans total
            </Typography>
          )}
        </Box>
      )}

      {/* Filter bar */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
        {!isCustomer && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Partner</InputLabel>
            <Select value={selectedCustomer} label="Partner" onChange={(e) => setSelectedCustomer(e.target.value)}>
              {customers.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={filterSeverity} label="Severity" onChange={(e) => setFilterSeverity(e.target.value)}>
            <MenuItem value="critical_high">Critical + High</MenuItem>
            <MenuItem value="all">All Severities</MenuItem>
            <MenuItem value="critical">Critical only</MenuItem>
            <MenuItem value="high">High only</MenuItem>
            <MenuItem value="medium">Medium only</MenuItem>
            <MenuItem value="low">Low only</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tier</InputLabel>
          <Select value={filterTier} label="Tier" onChange={(e) => setFilterTier(e.target.value)}>
            <MenuItem value="all">All Tiers</MenuItem>
            <MenuItem value="automatic">Auto-fixable</MenuItem>
            <MenuItem value="guided">Guided</MenuItem>
            <MenuItem value="managed">Managed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerFilter} label="Provider" onChange={(e) => setProviderFilter(e.target.value)}>
            <MenuItem value="all">All Providers</MenuItem>
            <MenuItem value="aws">AWS</MenuItem>
            <MenuItem value="gcp">GCP</MenuItem>
            <MenuItem value="azure">Azure</MenuItem>
            <MenuItem value="ssh">Private Server</MenuItem>
          </Select>
        </FormControl>
        {filterSeverity !== 'all' && pending.length > 0 && (
          <Link
            component="button"
            variant="body2"
            onClick={() => setFilterSeverity('all')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Show all issues ({pending.length})
          </Link>
        )}
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Pending${filteredPending.length > 0 ? ` (${filteredPending.length})` : ''}`} />
        <Tab label={`History${filteredHistory.length > 0 ? ` (${filteredHistory.length})` : ''}`} />
        <Tab label="Executive Report" icon={<SummarizeIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
      </Tabs>

      {/* ── Pending Fixes ──────────────────────────────────────────────────── */}
      {tab === 0 && (
        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : pendingGroups.length === 0 ? (
            filteredPending.length === 0 && pending.length > 0 ? (
              <Alert severity="info">
                No fixes match the current filters.{' '}
                <Link component="button" onClick={() => { setFilterSeverity('all'); setFilterTier('all'); setProviderFilter('all'); }}>
                  Clear all filters
                </Link>
              </Alert>
            ) : (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                No pending fix requests. Run a scan to find issues.
              </Alert>
            )
          ) : (
            pendingGroups.map((group) => (
              <ScanGroupCard
                key={group.scanId}
                group={group}
                mode="pending"
                expanded={expandedScans.has(group.scanId)}
                onToggle={() => toggleScan(group.scanId)}
                onApprove={approvefix}
                approving={approving}
                onCreateTicket={setTicketTarget}
                onRetry={retryFix}
                retrying={retrying}
              />
            ))
          )}
        </Box>
      )}

      {/* ── Fix History ────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : historyGroups.length === 0 ? (
            filteredHistory.length === 0 && history.length > 0 ? (
              <Alert severity="info">
                No history matches the current filters.{' '}
                <Link component="button" onClick={() => { setFilterSeverity('all'); setFilterTier('all'); setProviderFilter('all'); }}>
                  Clear all filters
                </Link>
              </Alert>
            ) : (
              <Alert severity="info">No fix history yet. Approve a pending fix to see results here.</Alert>
            )
          ) : (
            historyGroups.map((group) => (
              <ScanGroupCard
                key={group.scanId}
                group={group}
                mode="history"
                expanded={expandedScans.has(group.scanId)}
                onToggle={() => toggleScan(group.scanId)}
                onApprove={approvefix}
                approving={approving}
                onCreateTicket={setTicketTarget}
                onRetry={retryFix}
                retrying={retrying}
              />
            ))
          )}
        </Box>
      )}

      {/* ── Ticket Dialog ──────────────────────────────────────────────────── */}
      <CreateTicketDialog
        open={!!ticketTarget}
        onClose={() => setTicketTarget(null)}
        customerId={selectedCustomer}
        findingId={ticketTarget?.finding_id}
        previewTitle={ticketTarget?.finding_title ?? ''}
        previewPriority={
          ticketTarget?.finding_severity === 'critical' ? 'critical'
          : ticketTarget?.finding_severity === 'high' ? 'high'
          : ticketTarget?.finding_severity === 'medium' ? 'medium'
          : 'low'
        }
        previewProvider={ticketTarget?.provider ?? ''}
        previewResource={ticketTarget?.finding_resource_id ?? ''}
        previewScore={severityScore[ticketTarget?.finding_severity ?? 'low'] ?? 25}
      />

      {/* ── Executive Report ───────────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Generate a plain-English security summary written for business leaders &mdash; no technical jargon.
          </Typography>
          <Button
            variant="contained"
            startIcon={summaryLoading ? <CircularProgress size={14} color="inherit" /> : <SummarizeIcon />}
            onClick={generateSummary}
            disabled={summaryLoading || !selectedCustomer}
            sx={{ mb: 3 }}
          >
            Generate Security Report
          </Button>

          {summary && (
            <Paper elevation={0} sx={{ border: 1, borderColor: 'primary.dark', p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" color="primary" gutterBottom>Security Report</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" lineHeight={1.8} sx={{ whiteSpace: 'pre-wrap' }}>{summary}</Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}
